import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { PowerTile } from "@/components/design/PowerTile";
import { ProfileSilhouette } from "@/components/design/ProfileSilhouette";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import {
  POWERS_WEIGHT_MAX,
  POWERS_WEIGHT_MIN,
  POWER_GROUPS,
  POWER_WEIGHT_LEVELS,
  POWER_WEIGHT_LEVEL_MESSAGES,
  powerWeightLevel,
  type PowerWeightLevel,
} from "./match-design-rules";
import type { PowerGroupKey } from "./match-result-codec";
import type { MatchData } from "./match-design-types";

interface PowerProfileEditorProps {
  profile: MatchData["teamProfile"];
  onChange: (profile: MatchData["teamProfile"]) => void;
  readOnly?: boolean;
}

// Inline team-profile editor. Renders the ProfileSilhouette as two rows of
// three cluster cells; clicking a cluster label expands its 5 powers in a
// framed strip directly below that row, keeping the briefing compact and the
// silhouette-to-detail relationship visible (#31). The strip animates open
// and closed via the grid-rows 0fr/1fr pattern (same mechanic as StatTile's
// hover actions) and carries a caret pointing at the active cluster column,
// so the strip's cluster membership stays readable after the motion ends
// (#173). Each tile carries one readout, its relevance level as a word, and
// cycles to the next level when clicked; the stored 0..10 weight stays the
// wire representation. Edits autosave through onChange, no manual
// save/reset; re-selecting the preset in the dropdown is the reset path. See
// ADR-0017 (2026-07-29 amendment) and CONTEXT.md [[Power Profile Editor]].
export function PowerProfileEditor({
  profile,
  onChange,
  readOnly = false,
}: PowerProfileEditorProps) {
  const intl = useIntl();
  const [activeGroup, setActiveGroup] = useState<PowerGroupKey | null>(null);

  const weightLevelLabels = useMemo(
    () =>
      POWER_WEIGHT_LEVEL_MESSAGES.map((m) => intl.formatMessage(m)) as [
        string,
        string,
        string,
        string,
      ],
    [intl],
  );

  const ELEMENT_FROM_CODE = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [el, abbr] of Object.entries(ELEMENT_CODES)) out[abbr] = el;
    return out;
  }, []);

  const abbrWeights = useMemo(() => {
    const out: Record<string, number> = {};
    if (profile) {
      for (const [element, w] of Object.entries(profile.elementWeights)) {
        const abbr = ELEMENT_CODES[element];
        if (abbr) out[abbr] = w;
      }
    }
    return out;
  }, [profile]);

  function setWeight(abbr: string, value: number) {
    if (!profile) return;
    const element = ELEMENT_FROM_CODE[abbr];
    if (!element) return;
    const next = { ...profile.elementWeights };
    next[element] = Math.min(POWERS_WEIGHT_MAX, Math.max(POWERS_WEIGHT_MIN, value));
    onChange({ ...profile, elementWeights: next });
  }

  function toggle(key: PowerGroupKey) {
    setActiveGroup((current) => (current === key ? null : key));
  }

  // Bar taps open (never close) the cluster, so tapping around the
  // silhouette always lands on the matching strip.
  function openGroup(key: PowerGroupKey) {
    setActiveGroup(key);
  }

  const rows = [POWER_GROUPS.slice(0, 3), POWER_GROUPS.slice(3, 6)] as const;
  const weights = profile?.elementWeights ?? {};

  function renderTiles(abbrs: ReadonlyArray<string>) {
    return (
      <div className="grid grid-cols-3 gap-1.5 @sm:grid-cols-4 @lg:grid-cols-5">
        {abbrs.map((abbr) => {
          const p = POWER_BY_ABBR[abbr];
          if (!p) return null;
          // Wire-layer densifies on read (`match-design-mapping.ts`), so
          // `abbrWeights` always has an entry per power. The `?? 0` is a
          // defensive type-narrowing fallback for noUncheckedIndexedAccess;
          // it must match ProfileSilhouette's fallback so the bar and tile
          // can never disagree.
          const value = abbrWeights[abbr] ?? POWERS_WEIGHT_MIN;
          const level = powerWeightLevel(value);
          return (
            <PowerTile
              key={abbr}
              tag={p.tag}
              abbr={abbr}
              name={p.name}
              active
              // Same hover-reveal artwork as the Power Map. No rated-gate
              // here: the unlock mechanic belongs to Grow, in the briefing
              // the artwork is a recognition aid for every power.
              imageSrc={`/powers/${abbr.toLowerCase()}.jpg`}
              weightLevel={level}
              weightLevelLabels={weightLevelLabels}
              onWeightLevelSelect={
                readOnly
                  ? undefined
                  : (next: PowerWeightLevel) => {
                      // Re-selecting the current level is a no-op so bucketed
                      // preset values (e.g. 7 shown as Mittel) keep their
                      // stored value instead of normalising to the write value.
                      if (next === level) return;
                      setWeight(abbr, POWER_WEIGHT_LEVELS[next]);
                    }
              }
              className="power-tile--white"
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex}>
          <ProfileSilhouette
            elementWeights={weights}
            groups={row}
            activeCluster={activeGroup}
            onClusterClick={toggle}
            onBarClick={openGroup}
            className="px-1"
          />
          {/* One always-mounted container per cluster so opening AND closing
              animate (grid-rows 0fr/1fr needs the content in the DOM, see
              StatTile). `inert` keeps the collapsed tiles out of the tab
              order and the a11y tree. */}
          {row.map((group, columnIndex) => {
            const open = group.key === activeGroup;
            // Caret center = the cluster's column center in the 3-col grid
            // above: 1/6, 3/6, 5/6 of the row width.
            const caretCenter = `${(((columnIndex * 2 + 1) / 6) * 100).toFixed(3)}%`;
            return (
              <div
                key={group.key}
                inert={!open}
                className={`grid transition-all duration-500 ease-out ${
                  open ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="min-h-0 overflow-hidden">
                  <div className="relative pt-2">
                    {/* Two stacked triangles form the caret: the accent one is
                        the outline (signal color, matches the strip ring), the
                        surface one fills it and covers the ring hairline
                        underneath. */}
                    <span
                      aria-hidden
                      className="absolute top-0 border-x-8 border-b-8 border-x-transparent border-b-accent"
                      style={{ left: `calc(${caretCenter} - 8px)` }}
                    />
                    <span
                      aria-hidden
                      className="absolute top-[2px] z-10 border-x-[7px] border-b-[7px] border-x-transparent border-b-surface"
                      style={{ left: `calc(${caretCenter} - 7px)` }}
                    />
                    {/* box with the ring lifted to accent: the strip is the
                        one active editing surface on the step, so it carries
                        the signal color (same 1px weight as
                        .power-tile--active). ring-inset because the animated
                        wrapper is overflow-hidden and would clip an outset
                        ring on the left/right/bottom edges. */}
                    <div className="box ring-accent ring-inset p-2">{renderTiles(group.abbrs)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
